import KeenSlider from 'keen-slider'
import type { KeenSliderInstance, KeenSliderOptions } from 'keen-slider'

export default class PostsCarousel {
	private wrapper: HTMLElement
	private sliderRoot: HTMLElement | null = null
	private slider: KeenSliderInstance | null = null
	private prevBtn: HTMLButtonElement | null = null
	private nextBtn: HTMLButtonElement | null = null
	private navEl: HTMLElement | null = null
	private boundPrev: (() => void) | null = null
	private boundNext: (() => void) | null = null
	private infiniteLoop = true

	constructor(wrapper: HTMLElement) {
		this.wrapper = wrapper
	}

	init() {
		this.sliderRoot = this.wrapper.querySelector<HTMLElement>('.hg-posts-carousel__slider')
		if (!this.sliderRoot) return

		const slideCount = this.sliderRoot.querySelectorAll('.keen-slider__slide').length
		if (slideCount < 2) return

		this.infiniteLoop = this.wrapper.dataset.loop !== '0'

		const section = this.wrapper.closest<HTMLElement>('.hg-posts-carousel-section')
		this.prevBtn = section?.querySelector<HTMLButtonElement>('.hg-posts-carousel__nav--prev') ?? null
		this.nextBtn = section?.querySelector<HTMLButtonElement>('.hg-posts-carousel__nav--next') ?? null
		this.navEl = section?.querySelector<HTMLElement>('.hg-posts-carousel-section__actions') ?? null

		this.slider = new KeenSlider(this.sliderRoot, this.getOptions(slideCount))

		if (!this.infiniteLoop) {
			this.syncNav()
		}

		this.boundPrev = () => {
			this.slider?.prev()
		}
		this.boundNext = () => {
			this.slider?.next()
		}
		this.prevBtn?.addEventListener('click', this.boundPrev)
		this.nextBtn?.addEventListener('click', this.boundNext)
		window.addEventListener('resize', this.onResize)
	}

	cleanup() {
		if (this.boundPrev && this.prevBtn) {
			this.prevBtn.removeEventListener('click', this.boundPrev)
		}
		if (this.boundNext && this.nextBtn) {
			this.nextBtn.removeEventListener('click', this.boundNext)
		}
		window.removeEventListener('resize', this.onResize)
		this.boundPrev = null
		this.boundNext = null

		this.clearNavDisabled()

		this.slider?.destroy()
		this.slider = null
		this.navEl = null
	}

	private clearNavDisabled() {
		if (this.prevBtn) {
			this.prevBtn.disabled = false
			this.prevBtn.setAttribute('aria-disabled', 'false')
		}
		if (this.nextBtn) {
			this.nextBtn.disabled = false
			this.nextBtn.setAttribute('aria-disabled', 'false')
		}
		if (this.navEl) {
			this.navEl.hidden = false
		}
	}

	private syncNav = () => {
		if (this.infiniteLoop) return

		const d = this.slider?.track?.details
		if (!d) return

		const atStart = d.rel <= d.minIdx
		const atEnd = d.rel >= d.maxIdx

		if (this.prevBtn) {
			this.prevBtn.disabled = atStart
			this.prevBtn.setAttribute('aria-disabled', atStart ? 'true' : 'false')
		}
		if (this.nextBtn) {
			this.nextBtn.disabled = atEnd
			this.nextBtn.setAttribute('aria-disabled', atEnd ? 'true' : 'false')
		}

		if (this.navEl) {
			const hideNav =
				this.prevBtn !== null &&
				this.nextBtn !== null &&
				this.prevBtn.disabled &&
				this.nextBtn.disabled
			this.navEl.hidden = hideNav
		}
	}

	private onResize = () => {
		if (!this.sliderRoot || !this.slider) return

		const slideCount = this.sliderRoot.querySelectorAll('.keen-slider__slide').length
		this.slider.update(this.getOptions(slideCount))
		if (!this.infiniteLoop) {
			this.syncNav()
		}
	}

	private getOptions(slideCount: number): KeenSliderOptions {
		const spacing = this.getMainPaddingX()
		const loop = this.infiniteLoop && slideCount > 1

		const options: KeenSliderOptions = {
			loop,
			mode: 'snap',
			rubberband: true,
			slides: {
				perView: 1.25,
				spacing,
			},
			breakpoints: {
				'(min-width: 768px)': {
					slides: {
						perView: 2,
						spacing,
					},
				},
				'(min-width: 1200px)': {
					slides: {
						perView: 3,
						spacing,
					},
				},
			},
		}

		if (!this.infiniteLoop) {
			options.slideChanged = () => this.syncNav()
		}

		return options
	}

	private getMainPaddingX(): number {
		const root = document.documentElement
		const rootStyles = getComputedStyle(root)
		const raw = rootStyles.getPropertyValue('--main-padding-x').trim()
		const parsed = this.parseCssLength(raw, rootStyles)

		if (parsed !== null) return parsed

		const probe = document.createElement('div')
		probe.style.position = 'absolute'
		probe.style.visibility = 'hidden'
		probe.style.pointerEvents = 'none'
		probe.style.width = 'var(--main-padding-x)'
		root.appendChild(probe)
		const width = parseFloat(getComputedStyle(probe).width)
		probe.remove()

		return Number.isFinite(width) ? width : 0
	}

	private parseCssLength(value: string, rootStyles: CSSStyleDeclaration): number | null {
		if (!value) return null

		if (value.endsWith('px')) {
			const px = parseFloat(value)
			return Number.isFinite(px) ? px : null
		}

		if (value.endsWith('rem')) {
			const rem = parseFloat(value)
			const rootFontSize = parseFloat(rootStyles.fontSize)
			return Number.isFinite(rem) && Number.isFinite(rootFontSize) ? rem * rootFontSize : null
		}

		return null
	}
}
